import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Collapse, Drawer, Skeleton, Spin, Table, Tooltip as AntdTooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import styles from './customerInsightsDrawer.module.css';
import {
  fetchCustomerInsights,
  fetchCustomerKpis,
  fetchCustomerRevenueTrend,
  fetchCustomerListings,
} from '../../../services/api';
import SalesDocumentListingsDrawer, {
  ListingType as SalesListingType,
} from './SalesDocumentListingsDrawer';

type CustomerInsightsSummary = {
  period_sales?: number;
  period_orders?: number;
  avg_order_value?: number;
  today_sales?: number;
  this_week_sales?: number;
  this_month_sales?: number;
  this_year_sales?: number;
  last_year_sales?: number;
  open_so_amount?: number;
};

type CustomerInsights = {
  summary?: CustomerInsightsSummary;
  pipeline?: {
    stages?: Record<string, Record<string, number>>;
    conversion?: Record<string, number>;
    values?: Record<string, Record<string, number>>;
  };
  topSkus?: Array<{
    sku?: string | null;
    product_name?: string | null;
    product_id?: string | number | null;
    revenue?: number;
    last_ytd_revenue?: number;
    orders?: number;
    units?: number;
  }>;
  topSalesReps?: Array<{
    sales_rep_name?: string | null;
    revenue?: number;
    orders?: number;
  }>;
  targetPerformance?: Array<{
    year: number;
    target_amount: number;
    achievement_amount: number;
  }>;
};

type CustomerKpis = {
  core?: Record<string, number>;
  funnel?: Record<string, number>;
};

type RevenueTrendPoint = {
  label: string;
  revenue: number;
};

type RevenueTrendRange =
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'five_years';

type KpiTrendRow = {
  key: string;
  label: string;
  [key: string]: string | number | undefined;
};

type CustomerKpiTrendMetricRow = {
  key: string;
  metric: string;
  metricKey: string;
  [key: string]: string | number;
};

type ListingsResponse = {
  rows?: Array<Record<string, any>>;
  total?: number;
};

type CustomerInsightsDrawerProps = {
  open: boolean;
  onClose: () => void;
  customerId?: string | number | null;
  customerName?: string | null;
  dateFrom?: string;
  dateTo?: string;
};

type DateRange = {
  start: string;
  end: string;
};

const formatNumber = (value?: number) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '0';
  return Number(value).toLocaleString();
};

const formatCurrency = (value?: number) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatTooltipValue = (value: ValueType | undefined): string => {
  const raw = Array.isArray(value) ? value[0] : value;
  return formatCurrency(Number(raw ?? 0));
};

const formatOptionalCurrency = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  return formatCurrency(value);
};

const formatOptionalNumber = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  return formatNumber(value);
};

const formatPercentDisplay = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  return formatPercent(value);
};

const formatCompactCurrency = (value?: number) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '$0';
  const numeric = Number(value);
  if (numeric >= 1_000_000) return `$${(numeric / 1_000_000).toFixed(1)}M`;
  if (numeric >= 1_000) return `$${(numeric / 1_000).toFixed(1)}K`;
  return `$${numeric.toFixed(0)}`;
};

const formatDateDisplay = (value?: string | null) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatMonthLabel = (value: string) => {
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US', { month: 'short' });
  }
  const match = value.match(/[A-Za-z]{3,}/);
  if (match) {
    return match[0].slice(0, 3);
  }
  return value;
};

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatPercent = (value?: number) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '0%';
  return `${Number(value).toFixed(2)}%`;
};

const normalizeKpiTrendRow = (row: Record<string, any>): KpiTrendRow => {
  const normalized: KpiTrendRow = {
    key: String(row.key ?? row.label ?? row.id ?? Math.random()),
    label: String(row.label ?? row.key ?? ''),
  };
  Object.keys(row).forEach((key) => {
    if (key === 'key' || key === 'label' || key === 'id') {
      return;
    }
    const value = row[key];
    normalized[key] = typeof value === 'number' ? value : Number(value ?? 0);
  });
  return normalized;
};

const formatMetricLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatMonthRangeLabel = (label: string) => {
  const parsed = new Date(label);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const year = parsed.getFullYear();
  const monthIndex = parsed.getMonth();
  const startDate = new Date(year, monthIndex, 1);
  const endDate = new Date(year, monthIndex + 1, 0);
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
};

const KPI_METRIC_LABELS: Record<string, string> = {
  total_sales_amount: 'Sales Amount ($)',
  total_purchase_amount: 'Purchase Amount ($)',
  total_qty_sold: 'Qty Sold',
  gross_profit: 'Gross Profit ($)',
  gross_margin_percent: 'Gross Margin %',
  total_invoices: 'Invoices',
  total_orders: 'Orders',
  unique_skus: 'Unique SKUs',
  avg_order_value: 'Avg Deal Size ($)',
};

const KPI_METRIC_ORDER = [
  'total_sales_amount',
  'total_purchase_amount',
  'total_qty_sold',
  'gross_profit',
  'gross_margin_percent',
  'total_invoices',
  'total_orders',
  'unique_skus',
  'avg_order_value',
];

const KPI_METRIC_EXCLUDE = new Set([
  'range_start_label',
  'range_end_label',
  'range_start_date',
  'range_end_date',
]);

const getMetricFormat = (key: string) => {
  const lowered = key.toLowerCase();
  if (lowered.includes('percent') || lowered.includes('margin') || lowered.includes('ratio')) {
    return 'percent';
  }
  if (
    lowered.includes('amount') ||
    lowered.includes('revenue') ||
    lowered.includes('sales') ||
    lowered.includes('purchase') ||
    lowered.includes('profit') ||
    lowered.includes('avg') ||
    lowered.includes('deal')
  ) {
    return 'currency';
  }
  return 'number';
};

const calculatePercentageChange = (current?: number, previous?: number) => {
  if (previous === undefined || previous === null || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  if (current === undefined || current === null || !Number.isFinite(current)) return null;
  return ((current - previous) / previous) * 100;
};

const getTrendChange = (current?: number | null, previous?: number | null) => {
  if (current === undefined || current === null || previous === undefined || previous === null) {
    return null;
  }
  const delta = current - previous;
  if (!delta) return null;
  const baseline = previous === 0 ? Math.abs(current) : Math.abs(previous) || Math.abs(current);
  if (!baseline) return null;
  const percent = Math.abs((delta / baseline) * 100);
  if (!Number.isFinite(percent)) return null;
  return {
    direction: delta > 0 ? 'up' : 'down',
    percent,
  };
};

const CustomerInsightsDrawer: React.FC<CustomerInsightsDrawerProps> = ({
  open,
  onClose,
  customerId,
  customerName,
  dateFrom,
  dateTo,
}) => {
  const [insights, setInsights] = useState<CustomerInsights | null>(null);
  const [kpis, setKpis] = useState<CustomerKpis | null>(null);
  const [trendPoints, setTrendPoints] = useState<RevenueTrendPoint[]>([]);
  const [listings, setListings] = useState<ListingsResponse | null>(null);
  const [kpiTrendRows, setKpiTrendRows] = useState<KpiTrendRow[]>([]);
  const [kpiTrendLoading, setKpiTrendLoading] = useState(false);
  const focusAreaRange: RevenueTrendRange = 'this_year';
  const [focusRangeTrend, setFocusRangeTrend] = useState<RevenueTrendPoint[]>([]);
  const [focusRangeLoading, setFocusRangeLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pipelineListingsOpen, setPipelineListingsOpen] = useState(false);
  const [pipelineListingType, setPipelineListingType] = useState<SalesListingType>('sales_orders');
  const [pipelineListingRange, setPipelineListingRange] = useState<DateRange | null>(null);

  const canLoad = Boolean(open && customerId && dateFrom && dateTo);
  const formatAxisCurrency = formatCompactCurrency;

  const getPeriodRange = useCallback(
    (periodKey: string): DateRange => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let start = today;
      let end = today;

      const dayOfWeek = today.getDay();
      const mondayOffset = (dayOfWeek + 6) % 7;

      switch (periodKey) {
        case 'this_week': {
          start = new Date(today);
          start.setDate(today.getDate() - mondayOffset);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          break;
        }
        case 'this_month': {
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'last_month': {
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        case 'this_year': {
          start = new Date(today.getFullYear(), 0, 1);
          end = new Date(today.getFullYear(), 11, 31);
          break;
        }
        case 'last_year': {
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
          break;
        }
        case 'last_three_years': {
          start = new Date(today.getFullYear() - 2, 0, 1);
          end = new Date(today.getFullYear(), 11, 31);
          break;
        }
        default: {
          if (dateFrom && dateTo) {
            return { start: dateFrom, end: dateTo };
          }
        }
      }

      return { start: formatDateValue(start), end: formatDateValue(end) };
    },
    [dateFrom, dateTo],
  );

  const handleOpenPipelineListings = useCallback(
    (stageKey: string, periodKey: string) => {
      const listingMap: Record<string, SalesListingType | null> = {
        enquiries: 'enquiries',
        quotations: 'quotations',
        orders: 'sales_orders',
        invoices: 'sales_invoices',
      };
      const listingType = listingMap[stageKey];
      if (!listingType || !customerId) {
        return;
      }
      setPipelineListingType(listingType);
      setPipelineListingRange(getPeriodRange(periodKey));
      setPipelineListingsOpen(true);
    },
    [customerId, getPeriodRange],
  );

  const renderRevenueTooltip = useCallback(({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }
    const revenuePoint = payload.find((entry: any) => entry?.dataKey === 'revenue') ?? payload[0];
    const value = Number(revenuePoint?.value ?? 0);
    return (
      <div className={styles.customTooltip}>
        <div className={styles.customTooltipTitle}>{label}</div>
        <div className={styles.customTooltipRow}>
          <span>Revenue</span>
          <strong className={styles.customTooltipValue}>{formatCurrency(value)}</strong>
        </div>
      </div>
    );
  }, []);

  const loadData = useCallback(async () => {
    if (!canLoad || !customerId || !dateFrom || !dateTo) {
      return;
    }
    setLoading(true);
    try {
      const [insightsResponse, coreKpisResponse, trendResponse, listingsResponse] = await Promise.all([
        fetchCustomerInsights({
          customerId,
          dateFrom,
          dateTo,
          vmi: 'exclude',
          values: true,
        }),
        fetchCustomerKpis({
          customerId,
          dateFrom,
          dateTo,
          vmi: 'exclude',
        }),
        fetchCustomerRevenueTrend({
          customerId,
          dateFrom,
          dateTo,
          range: 'this_year',
          vmi: 'exclude',
        }),
        fetchCustomerListings({
          listingType: 'sales_orders',
          customerId,
          dateFrom,
          dateTo,
          sortBy: 'document_date',
          sortOrder: 'desc',
          limit: 25,
          offset: 0,
        }),
      ]);

      setInsights(insightsResponse ?? null);
      setKpis(coreKpisResponse ?? null);
      setTrendPoints((trendResponse?.points ?? []).map((point: any) => ({
        label: String(point.label ?? ''),
        revenue: Number(point.revenue ?? 0),
      })));
      setListings(listingsResponse ?? null);
    } finally {
      setLoading(false);
    }
  }, [canLoad, customerId, dateFrom, dateTo]);

  useEffect(() => {
    if (!open) {
      return;
    }
    loadData();
  }, [open, loadData]);

  const highlightCards = useMemo(() => {
    if (!kpis?.core) {
      return [];
    }
    const core = kpis.core;
    const summary = insights?.summary;
    return [
      {
        key: 'total_sales_amount',
        label: 'Total Sales Amount',
        value: formatOptionalCurrency(core.total_sales_amount),
        cardClass: styles.cardGold,
        iconClass: styles.iconGold,
        iconText: '$',
        details: [
          { label: 'Today', value: formatOptionalCurrency(summary?.today_sales) },
          { label: 'This Week', value: formatOptionalCurrency(summary?.this_week_sales) },
          { label: 'This Year', value: formatOptionalCurrency(summary?.this_year_sales) },
          { label: 'Last Year', value: formatOptionalCurrency(summary?.last_year_sales), tone: styles.positiveValue },
        ],
      },
      {
        key: 'total_purchase_amount',
        label: 'Purchase Amount',
        value: formatOptionalCurrency(core.total_purchase_amount),
        cardClass: styles.cardPink,
        iconClass: styles.iconPink,
        iconText: 'P',
        details: [
          { label: 'Gross Profit', value: formatOptionalCurrency(core.gross_profit) },
          { label: 'Margin %', value: formatPercentDisplay(core.gross_margin_percent) },
          {
            label: 'Total Qty Sold',
            value:
              core.total_qty_sold === null || core.total_qty_sold === undefined
                ? 'N/A'
                : `${formatNumber(core.total_qty_sold)} units`,
          },
        ],
      },
      {
        key: 'total_invoices',
        label: 'Total Invoices',
        value: formatOptionalNumber(core.total_invoices),
        cardClass: styles.cardBlue,
        iconClass: styles.iconBlue,
        iconText: 'I',
        details: [
          { label: 'Orders', value: formatOptionalNumber(summary?.period_orders) },
          { label: 'Avg Deal Size', value: formatOptionalCurrency(summary?.avg_order_value) },
          { label: 'Open SO Amount', value: formatOptionalCurrency(summary?.open_so_amount) },
          {
            label: 'Total Qty Sold',
            value:
              core.total_qty_sold === null || core.total_qty_sold === undefined
                ? 'N/A'
                : `${formatNumber(core.total_qty_sold)} units`,
          },
        ],
      },
      {
        key: 'unique_skus',
        label: 'Unique SKUs Purchased',
        value: formatOptionalNumber(core.unique_skus),
        cardClass: styles.cardLavender,
        iconClass: styles.iconLavender,
        iconText: 'U',
        details: [
          { label: 'Orders', value: formatOptionalNumber(summary?.period_orders) },
          { label: 'Total Invoices', value: formatOptionalNumber(core.total_invoices) },
          {
            label: 'Total Qty Sold',
            value:
              core.total_qty_sold === null || core.total_qty_sold === undefined
                ? 'N/A'
                : `${formatNumber(core.total_qty_sold)} units`,
          },
        ],
      },
    ];
  }, [insights?.summary, kpis?.core]);

  const stageOrder = [
    { key: 'enquiries', label: 'Enquiries' },
    { key: 'quotations', label: 'Quotations' },
    { key: 'orders', label: 'Sales Orders' },
    { key: 'invoices', label: 'Sales Invoices' },
  ];

  const periodOrder: Array<{ key: string; label: string }> = [
    { key: 'this_week', label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'this_year', label: 'This Year' },
    { key: 'last_year', label: 'Last Year' },
    { key: 'last_three_years', label: 'Last 3 Years' },
  ];

  const trendComparisonMap: Record<string, string | undefined> = {
    this_month: 'last_month',
    this_year: 'last_year',
  };

  useEffect(() => {
    if (!canLoad || !customerId || !dateFrom || !dateTo) {
      setKpiTrendRows([]);
      return;
    }
    setKpiTrendLoading(true);
    fetchCustomerKpis({
      customerId,
      dateFrom,
      dateTo,
      mode: 'kpi_trend',
      range: 'this_year',
      vmi: 'exclude',
    })
      .then((response) => {
        const payload = response?.data ?? response?.rows ?? response?.results ?? [];
        const normalized = Array.isArray(payload) ? payload.map((row: any) => normalizeKpiTrendRow(row)) : [];
        setKpiTrendRows(normalized);
      })
      .finally(() => {
        setKpiTrendLoading(false);
      });
  }, [canLoad, customerId, dateFrom, dateTo]);

  useEffect(() => {
    if (!canLoad || !customerId || !dateFrom || !dateTo) {
      setFocusRangeTrend([]);
      return;
    }
    setFocusRangeLoading(true);
    fetchCustomerRevenueTrend({
      customerId,
      dateFrom,
      dateTo,
      range: focusAreaRange,
      vmi: 'exclude',
    })
      .then((response) => {
        const points = (response?.points ?? []).map((point: any) => ({
          label: String(point.label ?? ''),
          revenue: Number(point.revenue ?? 0),
        }));
        setFocusRangeTrend(points);
      })
      .finally(() => {
        setFocusRangeLoading(false);
      });
  }, [canLoad, customerId, dateFrom, dateTo, focusAreaRange]);

  const focusRangeOptions: Array<{ key: RevenueTrendRange; label: string }> = [
    { key: 'this_year', label: 'This Year' },
    { key: 'last_year', label: 'Last Year' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'this_week', label: 'This Week' },
    { key: 'five_years', label: 'Last 5 Years' },
  ];

  const focusAreaRangeLabel = focusRangeOptions.find((option) => option.key === focusAreaRange)?.label ?? 'This Year';
  const focusAreaDateRange = useMemo(
    () => (dateFrom && dateTo ? { start: dateFrom, end: dateTo } : null),
    [dateFrom, dateTo],
  );

  const targetPerformanceData = [...(insights?.targetPerformance ?? [])]
    .map((row) => ({
      year: row.year,
      target_amount: Number(row.target_amount ?? 0),
      achievement_amount: Number(row.achievement_amount ?? 0),
    }))
    .sort((a, b) => a.year - b.year);

  const focusAreaView = useMemo(() => {
    const targetValue =
      targetPerformanceData.length > 0
        ? targetPerformanceData[targetPerformanceData.length - 1].target_amount
        : null;
    const totalAchieved = focusRangeTrend.reduce((sum, item) => sum + Number(item.revenue ?? 0), 0);
    const monthlyRevenueMap = focusRangeTrend.reduce<Record<string, number>>((acc, point) => {
      const label = formatMonthLabel(String(point.label ?? ''));
      acc[label] = (acc[label] ?? 0) + Number(point.revenue ?? 0);
      return acc;
    }, {});
    const lastDataIndex = monthLabels.reduce((lastIndex, label, index) => {
      return Object.prototype.hasOwnProperty.call(monthlyRevenueMap, label) ? index : lastIndex;
    }, -1);
    let runningTotal = 0;
    const monthlyPoints =
      focusAreaRange === 'this_year'
        ? monthLabels.map((label, index) => {
            const monthValue = monthlyRevenueMap[label] ?? 0;
            runningTotal += monthValue;
            return {
              label,
              Achieved: lastDataIndex !== -1 && index > lastDataIndex ? null : runningTotal,
              Target: targetValue !== null ? (targetValue / 12) * (index + 1) : 0,
            };
          })
        : focusRangeTrend.map((point) => ({
            label: point.label,
            Achieved: Number(point.revenue ?? 0),
            Target: targetValue ?? 0,
          }));
    const chartData =
      focusAreaRange === 'this_year'
        ? [{ label: '', Achieved: 0, Target: 0 }, ...monthlyPoints]
        : monthlyPoints;
    return {
      hasTarget: targetValue !== null,
      target: targetValue,
      total: totalAchieved,
      hasData: chartData.length > 0,
      chartData,
    };
  }, [focusRangeTrend, targetPerformanceData, focusAreaRange]);

  const focusYear = useMemo(() => new Date().getFullYear(), []);
  const thisYearSalesValue = Number(insights?.summary?.this_year_sales ?? 0);
  const avgDealSizeValue = Number(insights?.summary?.avg_order_value ?? 0);
  const isAnnualFocusRange = focusAreaRange === 'this_year';

  const thisYearTargetEntry = useMemo(
    () => targetPerformanceData.find((row) => row.year === focusYear) ?? null,
    [targetPerformanceData, focusYear],
  );
  const nextYearTargetEntry = useMemo(
    () => targetPerformanceData.find((row) => row.year === focusYear + 1) ?? null,
    [targetPerformanceData, focusYear],
  );
  const nextYearTarget = Number(nextYearTargetEntry?.target_amount ?? 0);
  const foresightYearLabel = nextYearTargetEntry?.year ?? focusYear + 1;
  const revenueGap = nextYearTarget > 0 ? nextYearTarget - thisYearSalesValue : null;
  const foresightRoadmap = revenueGap !== null ? { revenueGap } : null;

  const thisYearInsightTexts = useMemo<{
    targetGap: React.ReactNode;
    bestMonth: React.ReactNode;
    pipelineMonth: React.ReactNode;
    improvement: React.ReactNode;
  }>(() => {
    const emphasizeCurrency = (value?: number) => (
      <strong className={styles.thisYearNumber}>{formatCurrency(value ?? 0)}</strong>
    );
    const emphasizeNumber = (value?: number) => (
      <strong className={styles.thisYearNumber}>{formatNumber(value ?? 0)}</strong>
    );
    const emphasizeText = (value: string) => (
      <strong className={styles.thisYearNumber}>{value}</strong>
    );

    const targetValue = Number(thisYearTargetEntry?.target_amount ?? 0);
    const achievedValue = focusAreaView.total;
    const remainingTarget = targetValue - achievedValue;
    const monthsRemaining = Math.max(12 - (new Date().getMonth() + 1), 0);
    const avgNeeded = monthsRemaining > 0 ? remainingTarget / monthsRemaining : remainingTarget;

    const targetGapText =
      targetValue <= 0 ? (
        'No annual target configured for this customer.'
      ) : remainingTarget > 0 ? (
        <>
          Needs {emphasizeCurrency(remainingTarget)} more over the next{' '}
          {emphasizeNumber(monthsRemaining)} {monthsRemaining === 1 ? 'month' : 'months'} (~
          {emphasizeCurrency(avgNeeded)} per month) to hit the {emphasizeNumber(focusYear)} target of{' '}
          {emphasizeCurrency(targetValue)}.
        </>
      ) : remainingTarget < 0 ? (
        <>
          Already ahead of the {emphasizeNumber(focusYear)} target by{' '}
          {emphasizeCurrency(Math.abs(remainingTarget))}.
        </>
      ) : (
        'Tracking evenly with the annual target so far.'
      );

    const monthlyRevenueMap = focusRangeTrend.reduce<Record<string, number>>((acc, point) => {
      const label = formatMonthLabel(String(point.label ?? ''));
      acc[label] = (acc[label] ?? 0) + Number(point.revenue ?? 0);
      return acc;
    }, {});
    const bestMonthEntry = Object.entries(monthlyRevenueMap).reduce(
      (best, [label, value]) => (value > best.value ? { label, value } : best),
      { label: '', value: 0 },
    );
    const bestMonthText =
      bestMonthEntry.value > 0 ? (
        <>
          Best momentum came in {emphasizeText(bestMonthEntry.label)} with{' '}
          {emphasizeCurrency(bestMonthEntry.value)} booked - double down on the mix that worked.
        </>
      ) : (
        'Need more monthly wins to identify the standout playbook.'
      );

    const pipelineStages = insights?.pipeline?.stages ?? {};
    const monthEnquiries = Number(pipelineStages.enquiries?.this_month ?? 0);
    const monthQuotations = Number(pipelineStages.quotations?.this_month ?? 0);
    const monthOrders = Number(pipelineStages.orders?.this_month ?? 0);
    const monthInvoices = Number(pipelineStages.invoices?.this_month ?? 0);
    const billedThisMonth = Number(insights?.summary?.this_month_sales ?? 0);
    const pipelineMonthText =
      monthEnquiries + monthQuotations + monthOrders + monthInvoices > 0 ? (
        <>
          This month: {emphasizeNumber(monthEnquiries)} enquiries -{' '}
          {emphasizeNumber(monthQuotations)} quotes - {emphasizeNumber(monthOrders)} orders -{' '}
          {emphasizeNumber(monthInvoices)} invoices ({emphasizeCurrency(billedThisMonth)} billed).
        </>
      ) : (
        "Pipeline telemetry for the current month isn't available."
      );

    const conversionStages: Array<{
      label: string;
      fromValue: number;
      toValue: number;
    }> = [
      { label: 'Enquiry to Quote', fromValue: monthEnquiries, toValue: monthQuotations },
      { label: 'Quote to Order', fromValue: monthQuotations, toValue: monthOrders },
      { label: 'Order to Invoice', fromValue: monthOrders, toValue: monthInvoices },
    ];
    const weakest = conversionStages
      .map((stage) => ({
        ...stage,
        ratio: stage.fromValue > 0 ? stage.toValue / stage.fromValue : null,
      }))
      .filter((stage) => stage.ratio !== null)
      .sort((a, b) => (a.ratio ?? 0) - (b.ratio ?? 0))[0];

    const improvementText =
      weakest && weakest.ratio !== null ? (
        <>
          {emphasizeText(weakest.label)} is the biggest leakage at{' '}
          {emphasizeText(formatPercent(weakest.ratio * 100))} ({emphasizeNumber(weakest.toValue)} of{' '}
          {emphasizeNumber(weakest.fromValue)} deals). Coach tighter follow-ups here to recover revenue.
        </>
      ) : (
        'As soon as more pipeline activity flows in we will highlight the weakest step to focus on.'
      );

    return {
      targetGap: targetGapText,
      bestMonth: bestMonthText,
      pipelineMonth: pipelineMonthText,
      improvement: improvementText,
    };
  }, [
    focusAreaView.total,
    focusRangeTrend,
    focusYear,
    insights?.pipeline?.stages,
    insights?.summary?.this_month_sales,
    thisYearTargetEntry,
  ]);

  const roadmapStageRows = useMemo(() => {
    if (nextYearTarget <= 0 || avgDealSizeValue <= 0) {
      return [];
    }
    const stages = insights?.pipeline?.stages ?? {};
    const enquiries = Number(stages.enquiries?.this_year ?? 0);
    const quotations = Number(stages.quotations?.this_year ?? 0);
    const orders = Number(stages.orders?.this_year ?? 0);
    const invoices = Number(stages.invoices?.this_year ?? 0);
    const enquiryToQuote = enquiries > 0 ? quotations / enquiries : null;
    const quoteToOrder = quotations > 0 ? orders / quotations : null;
    const orderToInvoice = orders > 0 ? invoices / orders : null;

    const neededInvoices = Math.ceil(nextYearTarget / avgDealSizeValue);
    const neededOrders =
      orderToInvoice && orderToInvoice > 0 ? Math.ceil(neededInvoices / orderToInvoice) : null;
    const neededQuotes =
      quoteToOrder && quoteToOrder > 0 ? Math.ceil((neededOrders ?? 0) / quoteToOrder) : null;
    const neededEnquiries =
      enquiryToQuote && enquiryToQuote > 0 ? Math.ceil((neededQuotes ?? 0) / enquiryToQuote) : null;

    return [
      {
        key: 'enquiries',
        label: 'Enquiries',
        needed: neededEnquiries,
        baseline: enquiries,
        assumptionLabel: 'Quote rate',
        assumptionValue: enquiryToQuote ? formatPercent(enquiryToQuote * 100) : 'N/A',
      },
      {
        key: 'quotations',
        label: 'Quotations',
        needed: neededQuotes,
        baseline: quotations,
        assumptionLabel: 'Order rate',
        assumptionValue: quoteToOrder ? formatPercent(quoteToOrder * 100) : 'N/A',
      },
      {
        key: 'orders',
        label: 'Sales Orders',
        needed: neededOrders,
        baseline: orders,
        assumptionLabel: 'Invoice rate',
        assumptionValue: orderToInvoice ? formatPercent(orderToInvoice * 100) : 'N/A',
      },
      {
        key: 'invoices',
        label: 'Sales Invoices',
        needed: neededInvoices,
        baseline: invoices,
        assumptionLabel: 'Avg deal size',
        assumptionValue: formatCurrency(avgDealSizeValue),
      },
    ];
  }, [avgDealSizeValue, insights?.pipeline?.stages, nextYearTarget]);

  const canRenderRoadmap = roadmapStageRows.length > 0;

  const kpiTrendMetrics: Array<{
    key: CustomerKpiTrendMetricRow['metricKey'];
    label: string;
    format: 'currency' | 'number' | 'percent';
  }> = useMemo(() => {
    if (!kpiTrendRows.length) {
      return [];
    }
    const sample = kpiTrendRows[0];
    const availableKeys = new Set(
      Object.keys(sample).filter(
        (key) => key !== 'key' && key !== 'label' && !KPI_METRIC_EXCLUDE.has(key),
      ),
    );
    const orderedKeys = [
      ...KPI_METRIC_ORDER.filter((key) => availableKeys.has(key)),
      ...Array.from(availableKeys).filter((key) => !KPI_METRIC_ORDER.includes(key)),
    ];
    return orderedKeys.map((key) => ({
      key,
      label: KPI_METRIC_LABELS[key] ?? formatMetricLabel(key),
      format: getMetricFormat(key),
    }));
  }, [kpiTrendRows]);

  const kpiTrendTableData = useMemo<CustomerKpiTrendMetricRow[]>(() => {
    if (!kpiTrendRows.length) {
      return [];
    }
    return kpiTrendMetrics.map((metric) => {
      const row: CustomerKpiTrendMetricRow = {
        key: metric.key,
        metric: metric.label,
        metricKey: metric.key,
      };
      kpiTrendRows.forEach((period) => {
        row[period.key] = Number((period as Record<string, unknown>)[metric.key] ?? 0);
      });
      return row;
    });
  }, [kpiTrendMetrics, kpiTrendRows]);

  const kpiTrendColumns = useMemo<ColumnsType<CustomerKpiTrendMetricRow>>(() => {
    const columns: ColumnsType<CustomerKpiTrendMetricRow> = [
      {
        title: 'Metric',
        dataIndex: 'metric',
        key: 'metric',
        fixed: 'left',
        render: (value: string) => <span className={styles.kpiTrendMetric}>{value}</span>,
      },
    ];
    kpiTrendRows.forEach((period, index) => {
      const previousKey = index > 0 ? kpiTrendRows[index - 1]?.key : null;
      const rangeLabel = formatMonthRangeLabel(String(period.label ?? ''));
      columns.push({
        title: rangeLabel ? (
          <AntdTooltip title={rangeLabel}>
            <span>{period.label}</span>
          </AntdTooltip>
        ) : (
          <span>{period.label}</span>
        ),
        dataIndex: period.key,
        key: period.key,
        align: 'right',
        render: (value: unknown, record) => {
          const numericValue = Number(value ?? 0);
          const format = getMetricFormat(record.metricKey);
          const displayValue =
            format === 'currency'
              ? formatCurrency(numericValue)
              : format === 'percent'
              ? formatPercent(numericValue)
              : formatNumber(numericValue);
          const previousValue =
            previousKey && previousKey in record ? Number(record[previousKey] ?? 0) : null;
          const trendInfo =
            previousValue !== null ? getTrendChange(numericValue, previousValue) : null;
          return (
            <span className={styles.kpiTrendCell}>
              <span className={styles.kpiTrendValue}>{displayValue}</span>
              {trendInfo && (
                <span
                  className={`${styles.kpiTrendDelta} ${
                    trendInfo.direction === 'up' ? styles.kpiTrendUp : styles.kpiTrendDown
                  }`}
                >
                  {trendInfo.direction === 'up' ? '+' : '-'}
                  {formatPercent(trendInfo.percent)}
                </span>
              )}
            </span>
          );
        },
      });
    });
    return columns;
  }, [kpiTrendRows]);

  const topSalesRepColumns: ColumnsType<any> = [
    { title: 'Sales Rep', dataIndex: 'sales_rep_name', key: 'sales_rep_name' },
    { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (value) => formatCurrency(Number(value)) },
    { title: 'Orders', dataIndex: 'orders', key: 'orders', render: (value) => formatNumber(Number(value)) },
  ];

  const listingRows = listings?.rows ?? [];
  const listingColumns = useMemo<ColumnsType<any>>(() => {
    if (listingRows.length === 0) return [];
    const sample = listingRows[0];
    return Object.keys(sample).slice(0, 6).map((key) => ({
      title: key.replace(/_/g, ' ').toUpperCase(),
      dataIndex: key,
      key,
    }));
  }, [listingRows]);

  return (
    <Drawer
      title={
        <div className={styles.drawerHeaderCustom}>
          <div className={styles.drawerHeaderContent}>
            <div className={styles.drawerHeaderTitleRow}>
              <h2 className={styles.drawerHeaderTitle}>Client Insights</h2>
              {customerName && <span className={styles.drawerHeaderBadge}>{customerName}</span>}
            </div>
            <p className={styles.drawerHeaderSubtitle}>
              Performance summary for the selected date range
            </p>
          </div>
        </div>
      }
      placement="right"
      width={1400}
      open={open}
      onClose={onClose}
      destroyOnClose
      className={styles.overviewDrawer}
    >
      {!canLoad ? (
        <div>Please select a client and date range.</div>
      ) : loading ? (
        <div className={styles.drawerLoading}>
          <Spin />
        </div>
      ) : (
        <div className={styles.overviewDrawerContent}>
          {highlightCards.length > 0 && (
            <div className={styles.summaryCards}>
              {highlightCards.map((card) => (
                <div key={card.key} className={`${styles.summaryCard} ${card.cardClass}`}>
                  <div className={styles.summaryCardHeader}>
                    <div className={`${styles.summaryIcon} ${card.iconClass}`}>{card.iconText}</div>
                    <div className={styles.summaryHeaderText}>
                      <div className={styles.summaryCardTitle}>{card.label}</div>
                    </div>
                    <div className={styles.summaryValue}>{card.value}</div>
                  </div>
                  <div className={styles.summaryDivider} />
                  <div className={styles.summaryMetaList}>
                    {card.details.map((detail) => (
                      <div key={detail.label} className={styles.summaryMetaRow}>
                        <span>{detail.label}</span>
                        <strong className={detail.tone}>{detail.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <section className={styles.thisYearInsightsSection}>
            <Collapse
              defaultActiveKey={['customer-this-year']}
              bordered={false}
              className={styles.thisYearCollapse}
              items={[
                {
                  key: 'customer-this-year',
                  label: (
                    <div className={styles.collapseHeader}>
                      <div>
                        <span className={styles.collapseEyebrow}>Focus Area</span>
                        <div className={styles.sectionHeader}>This Year Insights</div>
                      </div>
                      <div className={styles.collapseSubtext}>
                        Cumulative performance ({focusAreaRangeLabel})
                      </div>
                    </div>
                  ),
                  children: (
                    <div className={styles.thisYearCollapseBody}>
                      {focusAreaDateRange?.start && focusAreaDateRange?.end && (
                        <div className={styles.focusRangeMeta}>
                          <span className={styles.focusRangeMetaLabel}>{focusAreaRangeLabel}</span>
                          <span className={styles.focusRangeDateText}>
                            {formatDateDisplay(focusAreaDateRange.start)} - {formatDateDisplay(focusAreaDateRange.end)}
                          </span>
                        </div>
                      )}
                      <div className={styles.thisYearTotalsRow}>
                        <div>
                          <span className={styles.thisYearInsightLabel}>Target</span>
                          <strong className={styles.thisYearTotalValue}>
                            {focusAreaView.hasTarget && focusAreaView.target !== null
                              ? formatCurrency(focusAreaView.target)
                              : 'Not available'}
                          </strong>
                        </div>
                        <div>
                          <span className={styles.thisYearInsightLabel}>Achieved</span>
                          <strong className={`${styles.thisYearTotalValue} ${styles.thisYearTotalAchieved}`}>
                            {formatCurrency(focusAreaView.total)}
                          </strong>
                        </div>
                      </div>
                      <div className={styles.thisYearChartWrapper}>
                        <div className={styles.thisYearChartContainer}>
                          {focusAreaView.hasData ? (
                            <ResponsiveContainer width="100%" height={260}>
                              <LineChart data={focusAreaView.chartData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                  dataKey="label"
                                  interval={0}
                                  tickFormatter={(value) => (value ? formatMonthLabel(String(value)) : '')}
                                />
                                <YAxis tickFormatter={(value) => formatCompactCurrency(value)} domain={[0, 'auto']} />
                                <RechartsTooltip
                                  formatter={(value: ValueType | undefined, name: NameType | undefined) => [
                                    formatTooltipValue(value),
                                    name === 'Target' ? 'Target' : 'Achieved',
                                  ]}
                                />
                                <Legend />
                                {focusAreaView.hasTarget && (
                                  <Line
                                    type="monotone"
                                    dataKey="Target"
                                    stroke="#4aa70c"
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2, stroke: '#ffffff', fill: '#4aa70c' }}
                                    activeDot={{ r: 6 }}
                                  />
                                )}
                                <Line
                                  type="monotone"
                                  dataKey="Achieved"
                                  stroke="#6366f1"
                                  strokeWidth={3}
                                  dot={{ r: 4, strokeWidth: 2, stroke: '#ffffff', fill: '#6366f1' }}
                                  activeDot={{ r: 6 }}
                                  connectNulls={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : focusRangeLoading ? (
                            <div className={styles.chartLoadingState}>
                              <Spin size="small" />
                            </div>
                          ) : (
                            <div className={styles.chartEmptyState}>
                              {focusAreaRange === 'this_year'
                                ? 'No annual targets or monthly revenue data available yet.'
                                : 'No revenue data available for the selected range yet.'}
                            </div>
                          )}
                        </div>
                      </div>
                      {isAnnualFocusRange && (
                        <div className={styles.thisYearInsightList}>
                          <div
                            className={`${styles.thisYearInsightItem} ${styles.thisYearInsightItemTarget}`}
                          >
                            <span className={`${styles.thisYearInsightLabel} ${styles.thisYearInsightTag}`}>
                              Target gap
                            </span>
                            <p>{thisYearInsightTexts.targetGap}</p>
                          </div>
                          <div
                            className={`${styles.thisYearInsightItem} ${styles.thisYearInsightItemStandout}`}
                          >
                            <span className={`${styles.thisYearInsightLabel} ${styles.thisYearInsightTag}`}>
                              Standout month
                            </span>
                            <p>{thisYearInsightTexts.bestMonth}</p>
                          </div>
                          <div
                            className={`${styles.thisYearInsightItem} ${styles.thisYearInsightItemPipeline}`}
                          >
                            <span className={`${styles.thisYearInsightLabel} ${styles.thisYearInsightTag}`}>
                              Pipeline pulse
                            </span>
                            <p>{thisYearInsightTexts.pipelineMonth}</p>
                          </div>
                          <div
                            className={`${styles.thisYearInsightItem} ${styles.thisYearInsightItemImprove}`}
                          >
                            <span className={`${styles.thisYearInsightLabel} ${styles.thisYearInsightTag}`}>
                              Improvement path
                            </span>
                            <p>{thisYearInsightTexts.improvement}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'customer-future-insights',
                  label: (
                    <div className={styles.collapseHeader}>
                      <div>
                        <span className={styles.collapseEyebrow}>Foresight</span>
                        <div className={styles.sectionHeader}>Performance & Forecast</div>
                      </div>
                      <div className={styles.collapseSubtext}>
                        This year's telemetry blended with predictive guidance
                      </div>
                    </div>
                  ),
                  children: (
                    <div className={styles.futureInsights}>
                      <div className={styles.foresightSummary}>
                        <div className={`${styles.foresightSummaryCard} ${styles.foresightSummaryTarget}`}>
                          <span>Next Year Target ({foresightYearLabel})</span>
                          <strong>
                            {nextYearTarget > 0 ? formatCurrency(nextYearTarget) : 'Not available'}
                          </strong>
                          <span className={styles.foresightSubtext}>Planned revenue goal</span>
                        </div>
                        <div className={`${styles.foresightSummaryCard} ${styles.foresightSummaryAchievement}`}>
                          <span>This Year Achievement</span>
                          <strong>{formatCurrency(thisYearSalesValue)}</strong>
                          <span className={styles.foresightSubtext}>YTD billed revenue</span>
                        </div>
                        <div
                          className={`${styles.foresightSummaryCard} ${styles.foresightSummaryGap} ${
                            foresightRoadmap
                              ? foresightRoadmap.revenueGap > 0
                                ? styles.foresightSummaryCardWarning
                                : styles.foresightSummaryCardPositive
                              : ''
                          }`}
                        >
                          <span>Gap vs Target</span>
                          <strong>
                            {foresightRoadmap
                              ? formatCurrency(Math.abs(foresightRoadmap.revenueGap))
                              : 'N/A'}
                          </strong>
                          <span className={styles.foresightSubtext}>
                            {foresightRoadmap
                              ? foresightRoadmap.revenueGap > 0
                                ? 'Additional revenue needed'
                                : 'Ahead of next year goal'
                              : 'Need target data'}
                          </span>
                        </div>
                        <div className={`${styles.foresightSummaryCard} ${styles.foresightSummaryAvg}`}>
                          <span>Avg Deal Size (TY)</span>
                          <strong>{avgDealSizeValue > 0 ? formatCurrency(avgDealSizeValue) : 'N/A'}</strong>
                          <span className={styles.foresightSubtext}>Roadmap assumptions</span>
                        </div>
                      </div>
                      {canRenderRoadmap ? (
                        <div className={styles.foresightRoadmap}>
                          {roadmapStageRows.map((stage) => {
                            const neededValue =
                              stage.needed !== null && stage.needed !== undefined ? stage.needed : null;
                            const baselineValue = stage.baseline ?? 0;
                            const delta = neededValue !== null ? neededValue - baselineValue : null;
                            const deltaText =
                              delta === null
                                ? 'Need conversion data'
                                : delta === 0
                                ? 'Keeps pace with TY'
                                : delta > 0
                                ? `+${formatNumber(delta)} vs TY`
                                : `${formatNumber(Math.abs(delta))} ahead of TY`;
                            const deltaClass =
                              delta === null
                                ? styles.roadmapStageDeltaNeutral
                                : delta > 0
                                ? styles.roadmapStageDeltaWarning
                                : delta < 0
                                ? styles.roadmapStageDeltaPositive
                                : styles.roadmapStageDeltaNeutral;
                            return (
                              <div
                                key={stage.key}
                                className={`${styles.roadmapStageCard} ${
                                  stage.key === 'invoices'
                                    ? styles.roadmapStageInvoices
                                    : stage.key === 'orders'
                                    ? styles.roadmapStageOrders
                                    : stage.key === 'quotations'
                                    ? styles.roadmapStageQuotes
                                    : styles.roadmapStageEnquiries
                                }`}
                              >
                                <div className={styles.roadmapStageHeader}>
                                  <div>
                                    <span>{stage.label}</span>
                                    <strong>{neededValue !== null ? formatNumber(neededValue) : 'N/A'}</strong>
                                  </div>
                                  <span className={`${styles.roadmapStageDelta} ${deltaClass}`}>
                                    {deltaText}
                                  </span>
                                </div>
                                <div className={styles.roadmapStageRow}>
                                  <span>TY actual</span>
                                  <span>{formatNumber(baselineValue)}</span>
                                </div>
                                <div className={styles.roadmapStageRow}>
                                  <span>{stage.assumptionLabel}</span>
                                  <span>{stage.assumptionValue}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className={styles.foresightEmpty}>
                          Need next year target, avg deal size, and pipeline conversion data to map the path.
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </section>

          <div className={styles.trendGrid}>
            <section className={styles.trendCard}>
              <div className={styles.trendHeader}>
                <div className={styles.sectionHeader}>Revenue Trend</div>
              </div>
              {loading ? (
                <>
                  <Skeleton active />
                  <Skeleton active />
                </>
              ) : trendPoints.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={trendPoints} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="customerRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#93c5fd" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#dbeafe" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12 }}
                      interval={0}
                      angle={-15}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tickFormatter={formatCompactCurrency} />
                    <RechartsTooltip content={renderRevenueTooltip} />
                    <Bar dataKey="revenue" fill="url(#customerRevenueGradient)" radius={[6, 6, 0, 0]} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.muted}>No revenue data for this range.</div>
              )}
            </section>

            <section className={styles.trendCard}>
              <div className={styles.trendHeader}>
                <div className={styles.sectionHeader}>Target vs Achievement</div>
              </div>
              {targetPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={targetPerformanceData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={formatAxisCurrency} />
                    <RechartsTooltip
                      formatter={(value: ValueType | undefined) => formatTooltipValue(value)}
                    />
                    <Legend />
                    <Bar dataKey="target_amount" name="Target" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="achievement_amount" name="Achieved" fill="#34d399" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.muted}>No target data available.</div>
              )}
            </section>
          </div>

          <section className={styles.pipelineCard}>
            <div className={styles.sectionHeader}>Pipeline Performance</div>
            <div className={styles.pipelineTable}>
              <div className={`${styles.pipelineRow} ${styles.pipelineHeader}`}>
                <span>Stage</span>
                {periodOrder.map((period) => (
                  <span key={period.key}>{period.label}</span>
                ))}
              </div>
              {stageOrder.map((stage) => (
                <div className={styles.pipelineRow} key={stage.key}>
                  <strong>{stage.label}</strong>
                  {periodOrder.map((period) => {
                    const currentValue = Number(insights?.pipeline?.stages?.[stage.key]?.[period.key] ?? 0);
                    const comparisonKey = trendComparisonMap[period.key];
                    const comparisonValue =
                      comparisonKey !== undefined
                        ? Number(insights?.pipeline?.stages?.[stage.key]?.[comparisonKey] ?? 0)
                        : null;
                    const isValueStage =
                      stage.key === 'quotations' || stage.key === 'orders' || stage.key === 'invoices';
                    const currentAmount = isValueStage
                      ? Number(insights?.pipeline?.values?.[stage.key]?.[period.key] ?? 0)
                      : null;
                    const comparisonAmount =
                      isValueStage && comparisonKey !== undefined
                        ? Number(insights?.pipeline?.values?.[stage.key]?.[comparisonKey] ?? 0)
                        : null;
                    const displayValue =
                      isValueStage && currentAmount !== null
                        ? formatCurrency(currentAmount)
                        : formatNumber(currentValue);
                    const valueToneClass =
                      stage.key === 'quotations'
                        ? styles.pipelineValueQuotation
                        : stage.key === 'orders'
                        ? styles.pipelineValueOrders
                        : stage.key === 'invoices'
                        ? styles.pipelineValueInvoices
                        : '';
                    const trendValue = isValueStage && currentAmount !== null ? currentAmount : currentValue;
                    const trendComparison =
                      isValueStage && comparisonAmount !== null ? comparisonAmount : comparisonValue;
                    const trendInfo =
                      comparisonKey !== undefined &&
                      period.key !== 'this_month' &&
                      period.key !== 'this_year'
                        ? getTrendChange(trendValue, trendComparison)
                        : null;
                    const conversionPercent =
                      isValueStage && currentAmount !== null
                        ? stage.key === 'quotations'
                          ? currentAmount > 0
                            ? (Number(insights?.pipeline?.values?.orders?.[period.key] ?? 0) / currentAmount) * 100
                            : 0
                          : stage.key === 'orders'
                          ? currentAmount > 0
                            ? (Number(insights?.pipeline?.values?.invoices?.[period.key] ?? 0) / currentAmount) * 100
                            : 0
                          : null
                        : null;
                    return (
                      <span key={`${stage.key}-${period.key}`} className={`${styles.pipelineValue} ${valueToneClass}`}>
                        <button
                          type="button"
                          className={`${styles.pipelineValueButton} ${valueToneClass}`}
                          onClick={() => handleOpenPipelineListings(stage.key, period.key)}
                        >
                          <span className={`${styles.pipelineValuePrimary} ${valueToneClass}`}>{displayValue}</span>
                        </button>
                        {conversionPercent !== null && (
                          <span className={styles.pipelineConversionValue}>{formatPercent(conversionPercent)}</span>
                        )}
                        {trendInfo && (
                          <span
                            className={`${styles.pipelineTrend} ${
                              trendInfo.direction === 'up' ? styles.pipelineTrendUp : styles.pipelineTrendDown
                            }`}
                          >
                            {trendInfo.direction === 'up' ? '+' : '-'} {formatPercent(trendInfo.percent)}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>

          <section className={styles.gridTwoColumns}>
            <div className={styles.skuCard}>
              <div className={styles.sectionHeader}>Top Performing SKUs</div>
              <div className={styles.skuList}>
                <div className={styles.skuHeaderRow}>
                  <div className={styles.skuHeaderProduct}>Product</div>
                  <div className={styles.skuHeaderRevenue}>YTD Revenue</div>
                  <div className={styles.skuHeaderLastYear}>Last YTD</div>
                  <div className={styles.skuHeaderChange}>Change</div>
                </div>
                {(insights?.topSkus ?? []).map((sku) => {
                  const label = sku.product_name || sku.sku || 'Unnamed SKU';
                  const percentageChange = calculatePercentageChange(sku.revenue, sku.last_ytd_revenue ?? 0);
                  const isPositive = percentageChange !== null && percentageChange >= 0;
                  const isNewProduct = (sku.last_ytd_revenue ?? 0) === 0 && (sku.revenue ?? 0) > 0;
                  return (
                    <div key={`${sku.sku ?? sku.product_name}`} className={styles.skuRow}>
                      <div className={styles.skuProductCol}>
                        <div className={styles.skuName}>{label}</div>
                        <div className={styles.skuMeta}>
                          {formatNumber(sku.units)} units * {formatNumber(sku.orders)} orders
                        </div>
                      </div>
                      <div className={styles.skuRevenueCol}>
                        <div className={styles.skuValue}>{formatCurrency(sku.revenue ?? 0)}</div>
                      </div>
                      <div className={styles.skuLastYearCol}>
                        <div className={styles.skuLastYearValue}>
                          {sku.last_ytd_revenue && sku.last_ytd_revenue > 0
                            ? formatCurrency(sku.last_ytd_revenue)
                            : '-'}
                        </div>
                      </div>
                      <div className={styles.skuChangeCol}>
                        {isNewProduct ? (
                          <div className={styles.skuChangeNew}>New</div>
                        ) : percentageChange !== null ? (
                          <div
                            className={`${styles.skuChange} ${
                              isPositive ? styles.skuChangePositive : styles.skuChangeNegative
                            }`}
                          >
                            <span className={styles.changeIcon}>{isPositive ? '+' : '-'}</span>
                            {formatPercent(Math.abs(percentageChange))}
                          </div>
                        ) : (
                          <div className={styles.skuChangeNA}>-</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(!insights?.topSkus || insights.topSkus.length === 0) && (
                  <div className={styles.muted}>No SKU data for the selected range.</div>
                )}
              </div>
            </div>
            <div className={styles.highlightCard}>
              <div className={styles.sectionHeader}>Top Performing Sales Reps</div>
              {(insights?.topSalesReps ?? []).length === 0 ? (
                <div className={styles.muted}>No sales rep data available.</div>
              ) : (
                <Table
                  className={styles.highlightTable}
                  columns={topSalesRepColumns}
                  dataSource={insights?.topSalesReps ?? []}
                  pagination={false}
                  size="small"
                  rowKey={(record) => record.sales_rep_id?.toString() ?? `sales-rep-${record.sales_rep_name}`}
                />
              )}
            </div>
          </section>

          {(kpiTrendLoading || kpiTrendTableData.length > 0) && (
            <section className={styles.kpiTrendSection}>
              <div className={styles.kpiTrendsHeader}>
                <div className={styles.sectionHeader}>Core KPI Trends</div>
              </div>
              {kpiTrendLoading ? (
                <>
                  <Skeleton active />
                  <Skeleton active />
                  <Skeleton active />
                </>
              ) : kpiTrendTableData.length === 0 ? (
                <div className={styles.muted}>No KPI trend data available.</div>
              ) : (
                <div className={styles.kpiTrendTableWrapper}>
                  <Table<CustomerKpiTrendMetricRow>
                    className={styles.kpiTrendTable}
                    dataSource={kpiTrendTableData}
                    columns={kpiTrendColumns}
                    pagination={false}
                    size="small"
                    rowKey="key"
                    scroll={{ x: 'max-content' }}
                  />
                </div>
              )}
            </section>
          )}

          {listingRows.length > 0 && (
            <Card size="small" title="Sales Orders" className={styles.tableCard}>
              <Table
                dataSource={listingRows}
                columns={listingColumns}
                size="small"
                pagination={false}
                rowKey={(row, index) => row.id ?? row.order_id ?? index}
              />
            </Card>
          )}

          {pipelineListingRange && customerId && (
            <SalesDocumentListingsDrawer
              open={pipelineListingsOpen}
              listingType={pipelineListingType}
              customerId={customerId}
              customerName={customerName ?? null}
              dateRange={pipelineListingRange}
              onClose={() => setPipelineListingsOpen(false)}
            />
          )}

        </div>
      )}
    </Drawer>
  );
};

export default CustomerInsightsDrawer;
